import {
  BACKGAMMON_MATCH_LENGTHS,
  CHESS_COLORS,
  CHESS_DISCONNECT_POLICIES,
  CHESS_TIME_CONTROLS,
  DEFAULT_BACKGAMMON_SETTINGS,
  DEFAULT_CHESS_SETTINGS,
  DEFAULT_PASUR_TARGET,
  DEFAULT_SHELEM_TARGET,
  turnTimerChoicesFor,
  type BackgammonSettings,
  type BackgammonMatchLength,
  type ChessColor,
  type ChessDisconnectPolicy,
  type ChessSettings,
  type ChessTimeControl,
  DEFAULT_HAFTKHABIS_TARGET,
  type HaftkhabisTarget,
  type PasurTarget,
  type ShelemTarget,
} from "@bing/shared";
import { useState, type ReactNode, type SVGProps } from "react";
import { DeckPicker } from "../components/DeckPicker";
import { HokmMatchLengthSelect } from "../components/HokmMatchLengthSelect";
import { Reveal } from "../components/Reveal";
import { SegmentedChoice } from "../components/SegmentedChoice";
import {
  DEFAULT_HOKM_MATCH_LENGTH,
  DEFAULT_HOKM_MODE_KOT,
  DEFAULT_HOKM_MODES,
  type HokmMatchLength,
} from "@bing/shared";
import { GameIcon } from "../components/GameIcon";
import { GAMES } from "../config";
import { DEFAULT_DOOZ_MATCH_LENGTH, GAME_CATEGORY, type GameId } from "@bing/shared";
import type { MenschPlayerCount } from "@bing/engine-mensch";
import { PASUR_SEAT_COUNTS, type PasurSeatCount } from "@bing/engine-pasur";
import {
  DEFAULT_LADDERS_LEVEL,
  DEFAULT_LADDERS_STYLE,
  type LaddersPlayerCount,
} from "@bing/engine-ladders";
import { DEFAULT_PASUR_SEATS } from "../game/room-sizes";
import { DoozColorSelect, DoozHandsSelect } from "./DoozTable";
import type { DoozPieceColor } from "../game/dooz-colors";
import { MenschPlayersSelect } from "./MenschTable";
import {
  LADDERS_LEVEL_CHOICES,
  LADDERS_PLAYER_CHOICES,
  LADDERS_STYLE_CHOICES,
} from "./LaddersTable";
import { BackgammonColorSelect } from "./BackgammonTable";
import {
  getBackgammonColor,
  setBackgammonColor,
  getDoozColor,
  setDoozColor,
  type BackgammonCheckerColor,
} from "../settings/prefs";
import { getMenschPlayersOnline, setMenschPlayersOnline } from "../settings/prefs";
import { DEFAULT_LADDERS_SEATS } from "../settings/prefs";
import { DEFAULT_TABLE_SETTINGS } from "../game/private-table";
import {
  HAFTKHABIS_TARGET_NUMBER_CHOICES,
  PASUR_TARGET_NUMBER_CHOICES,
} from "../game/match-targets";
import { SHELEM_TARGET_CHOICES } from "../game/shelem-targets";
import type { TableSettings } from "../game/private-table";
import { fa } from "../i18n/fa";
import { playSfx } from "../sound/sfx";
import { useSession } from "../store/context";

/* ------------------------------------------------------------------ *
 * Host's pre-create Game Settings screen — a Tailwind/Stitch port of the
 * "Table Settings" sheet (docs/stitch/17-game-settings.html): a gold-titled
 * app bar over three felt cards (a segmented turn-timer, an abandon-policy
 * radio pair, and a bot-fill switch) with a pinned full-width gold "create"
 * CTA. Presentation only — the `TableSettings` shape and the
 * `createPrivate(...)` wiring are untouched, so the private-table flow keeps
 * working; only the felt/gold Stitch chrome replaces the old inline styles.
 * ------------------------------------------------------------------ */

const FA_DIGITS = "۰۱۲۳۴۵۶۷۸۹";
function toFa(n: number): string {
  return String(n).replace(/\d/g, (d) => FA_DIGITS[Number(d)] ?? d);
}

type IconName =
  | "timer"
  | "sound"
  | "chat"
  | "cards"
  | "add"
  | "contrast"
  | "disconnect"
  | "seats"
  | "level"
  | "look";

/**
 * Inline `currentColor` SVGs standing in for the Stitch Material Symbols
 * (back/check reuse the shared `GameIcon`, per the design brief).
 */
function Icon({ name, className }: { name: IconName; className?: string }): ReactNode {
  const svg: SVGProps<SVGSVGElement> = {
    viewBox: "0 0 24 24",
    fill: "currentColor",
    className,
    "aria-hidden": true,
  };
  switch (name) {
    case "timer": // clock face + hands + top stem
      return (
        <svg {...svg}>
          <path d="M10 2h4v2h-4z" />
          <path d="M12 6a8 8 0 1 0 0 16 8 8 0 0 0 0-16zm.9 8.6-3.4-2 .8-1.4 2.6 1.5V9.5h1.8v5.1z" />
        </svg>
      );
    case "seats": // a small group — how many seats the table has (منچ)
      return (
        <svg {...svg}>
          <path d="M9 11.2a3.4 3.4 0 1 0 0-6.8 3.4 3.4 0 0 0 0 6.8zm0 1.9c-3.1 0-6.2 1.6-6.2 4.1V20h12.4v-2.8c0-2.5-3.1-4.1-6.2-4.1z" />
          <path
            d="M17.2 11.6a2.6 2.6 0 1 0 0-5.2 2.6 2.6 0 0 0 0 5.2zm0 1.6c-.7 0-1.4.1-2 .3 1.4.9 2.3 2.2 2.3 3.7V20H22v-2.8c0-2.1-2.4-4-4.8-4z"
            opacity="0.55"
          />
        </svg>
      );
    case "sound": // muted speaker (volume_off) — the owner's «بی‌صدا کردن میز»
      return (
        <svg {...svg}>
          <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3 3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4 9.91 6.09 12 8.18V4z" />
        </svg>
      );
    case "chat": // speech bubble
      return (
        <svg {...svg}>
          <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z" />
        </svg>
      );
    case "cards": // two overlapping cards
      return (
        <svg {...svg}>
          <path d="M3.6 7.6 9 5.3a2 2 0 0 1 2.6 1l4 9.3-7.4 3.2a2 2 0 0 1-2.6-1L2 9.2a2 2 0 0 1 .9-1.3zm14.4-.6a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-3.1l1.4-.6a3.5 3.5 0 0 0 1.8-4.6L14.3 7z" />
        </svg>
      );
    case "add": // add_circle
      return (
        <svg {...svg}>
          <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4z" />
        </svg>
      );
    case "contrast": // half-filled disc — the chess «رنگ مهره‌ها» (white vs black side)
      return (
        <svg {...svg}>
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18V4c4.41 0 8 3.59 8 8s-3.59 8-8 8z" />
        </svg>
      );
    case "disconnect": // power symbol — the chess «قطع اتصال» policy
      return (
        <svg {...svg}>
          <path d="M13 3h-2v10h2V3zm4.83 2.17-1.42 1.42C17.99 7.86 19 9.81 19 12c0 3.87-3.13 7-7 7s-7-3.13-7-7c0-2.19 1.01-4.14 2.58-5.42L6.17 5.17C4.23 6.82 3 9.26 3 12c0 4.97 4.03 9 9 9s9-4.03 9-9c0-2.74-1.23-5.18-3.17-6.83z" />
        </svg>
      );
    case "level": // three rising bars (signal_cellular_alt) — مار پله's «سطح بازی»
      return (
        <svg {...svg}>
          <path d="M17 4h3v16h-3V4zM5 14h3v6H5v-6zm6-5h3v11h-3V9z" />
        </svg>
      );
    case "look": // a painter's palette — مار پله's «ظاهر صفحه»
      return (
        <svg {...svg}>
          <path d="M12 3a9 9 0 0 0 0 18c.83 0 1.5-.67 1.5-1.5 0-.39-.15-.74-.39-1.01-.23-.26-.38-.61-.38-.99 0-.83.67-1.5 1.5-1.5H16c2.76 0 5-2.24 5-5 0-4.42-4.03-8-9-8zm-5.5 9c-.83 0-1.5-.67-1.5-1.5S5.67 9 6.5 9 8 9.67 8 10.5 7.33 12 6.5 12zm3-4C8.67 8 8 7.33 8 6.5S8.67 5 9.5 5s1.5.67 1.5 1.5S10.33 8 9.5 8zm5 0c-.83 0-1.5-.67-1.5-1.5S13.67 5 14.5 5s1.5.67 1.5 1.5S15.33 8 14.5 8zm3 4c-.83 0-1.5-.67-1.5-1.5S16.67 9 17.5 9s1.5.67 1.5 1.5-.67 1.5-1.5 1.5z" />
        </svg>
      );
  }
}

/**
 * Stitch pill switch — a `role="switch"` button (accessible + testable),
 * mirroring `Profile`'s toggle but gold-on to match this screen's design.
 * («سودوکو»'s «بررسی اشتباه» on its start card uses it too.)
 */
export function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
}): ReactNode {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={onChange}
      className={`relative h-6 w-11 shrink-0 appearance-none cursor-pointer rounded-full border-0 transition-colors ${
        checked ? "bg-gold-deep" : "bg-surface-variant"
      }`}
    >
      <span
        aria-hidden
        // The knob is ALWAYS the high-contrast on-surface colour (near-white on the dark
        // theme) so the OFF state is clearly visible — it used to be surface-container-
        // highest, nearly the same grey as the OFF track, so the knob "disappeared".
        className="absolute top-0.5 h-5 w-5 rounded-full bg-on-surface shadow"
        style={{ insetInlineStart: checked ? 22 : 2, transition: "inset-inline-start .2s ease" }}
      />
    </button>
  );
}

/** A felt card matching the design's blurred low-surface `section`. */
function Card({
  className = "",
  children,
}: {
  className?: string;
  children: ReactNode;
}): ReactNode {
  return (
    <section
      className={`rounded-xl border border-outline-variant/30 bg-surface-container-low/80 p-3.5 shadow-lg backdrop-blur-md ${className}`}
    >
      {children}
    </section>
  );
}

/** A card heading: a gold Material-symbol glyph + label (design's `h2`). */
function Heading({ icon, children }: { icon: IconName; children: ReactNode }): ReactNode {
  return (
    <h2 className="m-0 flex items-center gap-sm font-body-lg text-body-lg text-on-surface">
      <span className="text-gold-deep">
        <Icon name={icon} className="h-5 w-5" />
      </span>
      {children}
    </h2>
  );
}

/**
 * A single-select segmented card — the chess settings pickers (colour / time control /
 * disconnect policy), تخته نرد's disconnect policy and شلم's match target reuse the turn-timer's
 * segmented `radiogroup` look, wrapping when the labels don't fit one row. `T` is the stored
 * value — a string key, or a number (شلم's ۶۶۰ / ۱۲۰۰) — and each option carries its own label.
 * The control itself is {@link SegmentedChoice} (card-67: the شلم solo start screen reuses it).
 */
function ChoiceRow<T extends string | number>({
  icon,
  label,
  options,
  value,
  onChange,
  hint,
  hint2,
  oneLine = false,
  wrapLabels = false,
}: {
  icon: IconName;
  label: string;
  options: readonly (readonly [T, string])[];
  value: T;
  onChange: (value: T) => void;
  /** ITEM 11 — a caption under the choices, for a row whose labels are not self-explanatory
   *  (chess's «۱۵+۱۰» says nothing about what the two numbers are). */
  hint?: string;
  /** ITEM 9 — a SECOND caption line, rendered under {@link hint}. One number per line: a
   *  single long sentence wrapped wherever the box ended and split mid-clause. */
  hint2?: string;
  /** ITEM 11 — keep every choice on ONE line. Five time controls wrapped onto two rows, which
   *  read as two separate groups; the chips shrink to fit instead. */
  oneLine?: boolean;
  /** A label wider than its button wraps inside it ({@link SegmentedChoice}'s `wrapLabels`). */
  wrapLabels?: boolean;
}): ReactNode {
  return (
    <Card>
      <Heading icon={icon}>{label}</Heading>
      <SegmentedChoice
        label={label}
        options={options}
        value={value}
        onChange={onChange}
        oneLine={oneLine}
        wrapLabels={wrapLabels}
        className="mt-1.5"
      />
      {hint ? <p className="mt-1.5 m-0 text-caption text-on-surface-variant">{hint}</p> : null}
      {hint2 ? <p className="mt-0.5 m-0 text-caption text-on-surface-variant">{hint2}</p> : null}
    </Card>
  );
}

export function GameSettings(): ReactNode {
  const activeGame = useSession((s) => s.activeGame);
  const setView = useSession((s) => s.setView);
  const createPrivate = useSession((s) => s.createPrivate);
  const isChess = activeGame === "chess";
  const isMensch = activeGame === "mensch";
  const isBackgammon = activeGame === "backgammon";
  const isDooz = activeGame === "dooz";
  const isHokm = activeGame === "hokm";
  const isShelem = activeGame === "shelem";
  const isPasur = activeGame === "pasur";
  const isLadders = activeGame === "ladders";
  const isHaftkhabis = activeGame === "haftkhabis";
  // The card-back picker belongs to games that HAVE cards. It used to render for anything
  // that was not chess, so منچ and تخته نرد were both offering a card design for a table with
  // no cards on it. Each game's table settings must be ITS OWN.
  const usesCards = GAME_CATEGORY[activeGame as GameId] === "card";
  // دوز's piece colour is VIEWER-SIDE (تخته نرد's model), so it is remembered locally and never
  // put on the wire — the host picks how their OWN pieces look, and so does their guest.
  const [doozColor, setDoozColorLocal] = useState<DoozPieceColor>(() => getDoozColor());
  const [menschPlayers, setMenschPlayersLocal] = useState<MenschPlayerCount>(() =>
    getMenschPlayersOnline(),
  );
  // پاسور — the table's SIZE, ۴ or ۲ (operator, 2026-10-20). ALWAYS opening on ۴, the operator's
  // «default 4»: no per-device memory, unlike منچ's. A size is a ROOM, so it rides the create intent
  // (`createPrivate`'s `seats`), never `settings`.
  const [pasurSeats, setPasurSeats] = useState<PasurSeatCount>(DEFAULT_PASUR_SEATS);
  // مار پله — the same rule as پاسور's (card-86): ۴ or ۲, opening on ۴ every time, riding the create
  // intent (a size is a ROOM: `ladders2-private` / `ladders-private`), never `settings`.
  const [laddersSeats, setLaddersSeats] = useState<LaddersPlayerCount>(DEFAULT_LADDERS_SEATS);
  // ITEM 12 — تخته نرد checker colour (a per-viewer preference, not a table rule).
  const [bgColor, setBgColor] = useState<BackgammonCheckerColor>(() => getBackgammonColor());
  // Chess tables seed a chess block; card tables never carry one (their settings are
  // unchanged). The chess block rides create → the lobby echo → the read-only chips.
  const [settings, setSettings] = useState<TableSettings>(() => {
    // ITEM 7 — seed the game's OWN default turn timer (منچ 10s, card games 25s). Seeding the
    // card default on a منچ table would show 25 preselected and then be clamped away server-side.
    const base: TableSettings = {
      ...DEFAULT_TABLE_SETTINGS,
      turnTimerSec: turnTimerChoicesFor(activeGame).fallback as TableSettings["turnTimerSec"],
    };
    /*
     * حکم SEEDS ITS MATCH LENGTH — the picker below renders `settings.matchLength ??
     * DEFAULT_HOKM_MATCH_LENGTH`, so it SHOWS the default and only WRITES on change. A host who
     * accepts the seven the screen already offers therefore used to send nothing at all: the table
     * ran seven rounds (the engine's own default, so play was always right) but carried no
     * `matchLength`, so the room published none and the admin card's «🎯 طول مسابقه» line vanished
     * on exactly the private tables where a host had chosen. Measured on prod: 21/21 public tables
     * carried a length, 4/7 private ones did. Seeded here for the same reason chess and تخته نرد
     * seed their blocks — a screen that offers a choice should assert its default, not imply it.
     */
    // Seeded for the same reason `matchLength` is: the picker/toggle RENDER the default, so a
    // host who accepts what the screen already shows must still SEND it — otherwise the room has
    // nothing to publish and a chosen-looking setting is silently absent.
    if (isHokm)
      return {
        ...base,
        matchLength: DEFAULT_HOKM_MATCH_LENGTH,
        hokmModeKot: DEFAULT_HOKM_MODE_KOT,
        hokmModes: DEFAULT_HOKM_MODES,
      };
    // دوز SEEDS ITS HAND COUNT for exactly the reason حکم seeds its match length: the picker
    // RENDERS the default and only WRITES on change, so a host who accepts the three the screen
    // already offers would otherwise send nothing — the table would publish no length and the
    // invitation would silently lose its «🎯 بازی تا ۳ دست» line.
    if (isDooz) return { ...base, matchLength: DEFAULT_DOOZ_MATCH_LENGTH };
    // شلم SEEDS ITS TARGET for the same reason: the picker RENDERS ۶۶۰ (the default since card-67) and
    // only WRITES on change, so a host who accepts it would otherwise send nothing — and the waiting
    // room's chip reads it.
    if (isShelem) return { ...base, shelemTarget: DEFAULT_SHELEM_TARGET };
    // …and پاسور its (۶۲), for the same reason.
    if (isPasur) return { ...base, pasurTarget: DEFAULT_PASUR_TARGET };
    // …and هفت خبیث its (۱۰۰).
    if (isHaftkhabis) return { ...base, haftkhabisTarget: DEFAULT_HAFTKHABIS_TARGET };
    // مار پله SEEDS ITS LEVEL AND LOOK for the same reason (round 2): both rows RENDER the default
    // (متوسط, «جنگل») and only WRITE on change, and the waiting room's chips read them.
    if (isLadders)
      return { ...base, laddersLevel: DEFAULT_LADDERS_LEVEL, laddersStyle: DEFAULT_LADDERS_STYLE };
    if (isChess) return { ...base, chess: DEFAULT_CHESS_SETTINGS };
    // تخته نرد carries its own block, so the host's clock + disconnect policy actually
    // reach the table (the server injects the same defaults when none is sent).
    if (isBackgammon) return { ...base, backgammon: DEFAULT_BACKGAMMON_SETTINGS };
    return base;
  });
  const chess = settings.chess ?? DEFAULT_CHESS_SETTINGS;
  const setChess = (patch: Partial<ChessSettings>): void =>
    setSettings((s) => ({ ...s, chess: { ...(s.chess ?? DEFAULT_CHESS_SETTINGS), ...patch } }));
  const backgammon = settings.backgammon ?? DEFAULT_BACKGAMMON_SETTINGS;
  const setBackgammon = (patch: Partial<BackgammonSettings>): void =>
    setSettings((s) => ({
      ...s,
      backgammon: { ...(s.backgammon ?? DEFAULT_BACKGAMMON_SETTINGS), ...patch },
    }));

  const gameTitle = GAMES.find((g) => g.id === activeGame)?.fa ?? "";
  const title = gameTitle ? `${fa.online.settings} - ${gameTitle}` : fa.online.settings;

  // Chess picker options (chess-only) — Persian labels, the preset keys preserved.
  const colorOptions = CHESS_COLORS.map((c): [ChessColor, string] => [
    c,
    c === "white"
      ? fa.chess.colorWhite
      : c === "black"
        ? fa.chess.colorBlack
        : fa.chess.colorRandom,
  ]);
  const timeOptions = (Object.keys(CHESS_TIME_CONTROLS) as ChessTimeControl[]).map(
    (k): [ChessTimeControl, string] => [
      k,
      k === "none" ? fa.chess.untimed : k.replace(/\d/g, (d) => FA_DIGITS[Number(d)] ?? d),
    ],
  );
  // ITEM 7 — this game's turn-timer choices (منچ's short set, or the card set).
  const { options: timerOptions } = turnTimerChoicesFor(activeGame);
  const dcOptions = CHESS_DISCONNECT_POLICIES.map((p): [ChessDisconnectPolicy, string] => [
    p,
    p === "claim" ? fa.chess.dcClaim : p === "bot" ? fa.chess.dcBot : fa.chess.dcPause,
  ]);
  // تخته نرد reuses the SAME disconnect-policy vocabulary (the server does too), with its
  // own labels so the two screens stay independently readable.
  const bgDcOptions = CHESS_DISCONNECT_POLICIES.map((p): [ChessDisconnectPolicy, string] => [
    p,
    p === "claim"
      ? fa.backgammon.dcClaim
      : p === "bot"
        ? fa.backgammon.dcBot
        : fa.backgammon.dcPause,
  ]);
  /** «۲ نفره» / «۴ نفره» — the engine's own sizes, worded as the lobby's two public rooms are. */
  const pasurSeatOptions = PASUR_SEAT_COUNTS.map((n): [PasurSeatCount, string] => [
    n,
    fa.pasur.seatsOption(toFa(n)),
  ]);
  /** «۱ دست» / «۳ دست» … — the دست IS the scoring unit (a مارس counts as two, سگ‌مارس three). */
  const bgMatchOptions = BACKGAMMON_MATCH_LENGTHS.map((n): [BackgammonMatchLength, string] => [
    n,
    fa.backgammon.matchLengthOption(toFa(n)),
  ]);

  /* Turn timer — a segmented single-select (radiogroup).
     ITEM 7 — the allowed set is PER GAME: منچ runs 5/10/15/20/25 (default 10), مار پله the same
     without 5 (default 10 — a roll's motion runs up to ~3.6 s), تخته نرد
     10/15/20/25 (default 15), every card game 15/25/35/45/60 (default 25). `turnTimerChoicesFor`
     is the same function the server clamps with, so this can never offer a value the server would
     rewrite. CHESS is the one game without it — its time control is a whole-game bank instead. */
  const turnTimerCard = (
    <Card>
      <Heading icon="timer">
        {fa.online.turnTimer}
        <span className="font-caption text-caption text-on-surface-variant">
          ({fa.online.seconds})
        </span>
      </Heading>
      <div
        role="radiogroup"
        aria-label={fa.online.turnTimer}
        className="mt-sm flex gap-1 rounded-lg bg-surface-container-highest p-1"
      >
        {timerOptions.map((sec) => {
          const on = settings.turnTimerSec === sec;
          return (
            <button
              key={sec}
              type="button"
              role="radio"
              aria-checked={on}
              aria-label={`${sec} ${fa.online.seconds}`}
              onClick={() =>
                setSettings((s) => ({
                  ...s,
                  turnTimerSec: sec as TableSettings["turnTimerSec"],
                }))
              }
              className={`flex-1 appearance-none cursor-pointer whitespace-nowrap rounded-md border-0 py-2 text-center font-body-md text-body-md transition-colors ${
                on
                  ? "bg-gold-deep text-ink-on-gold shadow"
                  : "bg-transparent text-on-surface-variant hover:text-on-surface"
              }`}
            >
              {toFa(sec)}
            </button>
          );
        })}
      </div>
      {isBackgammon ? (
        <p className="mt-0.5 font-caption text-caption leading-snug text-on-surface-variant">
          {fa.backgammon.turnTimerHint}
        </p>
      ) : null}
    </Card>
  );

  return (
    <div
      className="flex flex-col overflow-hidden bg-background text-on-surface"
      dir="rtl"
      style={{
        height: "var(--app-h)",
        backgroundImage:
          "radial-gradient(circle at 50% 0%, rgb(var(--c-felt-deep) / 0.35) 0%, rgb(var(--c-surface)) 70%)",
      }}
    >
      {/* Top app bar — gold title + back (design's fixed header). */}
      <header
        className="z-20 flex shrink-0 items-center gap-sm border-b border-outline-variant/40 bg-surface-container-low/90 px-md pb-3"
        style={{
          paddingTop: "calc(max(env(safe-area-inset-top), var(--tg-safe-top, 0px)) + 12px)",
          backdropFilter: "blur(12px)",
        }}
      >
        <button
          type="button"
          aria-label={fa.online.back}
          onClick={() => setView("lobby")}
          className="flex h-10 w-10 shrink-0 appearance-none items-center justify-center rounded-full border-0 bg-transparent text-gold-deep cursor-pointer transition-transform hover:bg-surface-variant/50 active:scale-95"
        >
          <GameIcon type="back" size={22} />
        </button>
        {/* هفت خبیث's name is the longest: a step down keeps its title whole at 320 px; every other game's as it was */}
        <h1
          className={`m-0 flex-1 truncate text-center text-gold-deep ${
            isHaftkhabis
              ? "font-body-lg text-body-lg font-bold"
              : "font-headline-sm text-headline-sm"
          }`}
        >
          {title}
        </h1>
        <div className="h-10 w-10 shrink-0" />
      </header>

      {/* Body — scrolls between the pinned bar and CTA. Mounts with the app's
          shared reveal (`bg-slidein`, like Guides/Sheet/WaitingRoom). */}
      <main
        className="mx-auto flex w-full max-w-container-max flex-1 flex-col gap-3 overflow-y-auto px-md py-3"
        style={{ animation: "bg-slidein 0.25s ease-out" }}
      >
        {isChess ? (
          <>
            {/* Chess: which side the host plays. */}
            <ChoiceRow
              icon="contrast"
              label={fa.chess.color}
              options={colorOptions}
              value={chess.color}
              onChange={(color) => setChess({ color })}
            />
            {/* ITEM 11 — chess time control: all five choices on ONE line (they used to wrap
                onto two, reading as two unrelated groups), plus a caption saying what the two
                numbers in «۱۵+۱۰» actually are. The label alone tells a new player nothing. */}
            <ChoiceRow
              icon="timer"
              label={fa.chess.timeControl}
              options={timeOptions}
              value={chess.timeControl}
              onChange={(timeControl) => setChess({ timeControl })}
              oneLine
              hint={fa.chess.timeControlHint}
              hint2={fa.chess.timeControlHint2}
            />
          </>
        ) : isBackgammon ? (
          /* تخته نرد: the MATCH LENGTH, then the PER-TURN window — the ONLY clock this game has.
             ⚠️ A whole-game time bank used to sit above these and it is GONE (operator, 2026-08-15):
             a backgammon turn is a throw the player did not choose plus a couple of taps, so a
             whole-game budget measures the dice as much as the thinking. Do not re-add it. */
          <>
            {/* MATCH LENGTH — «۵ دست» is the traditional length and the default. A مارس counts as
                two دست and a سگ‌مارس three, so the دست is both the round AND the scoring unit. */}
            <Card>
              <Heading icon="cards">{fa.backgammon.matchLength}</Heading>
              <div
                className="mt-sm grid gap-2"
                role="radiogroup"
                aria-label={fa.backgammon.matchLength}
                style={{ gridTemplateColumns: `repeat(${bgMatchOptions.length}, minmax(0, 1fr))` }}
              >
                {bgMatchOptions.map(([value, label]) => {
                  const on = backgammon.matchLength === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      onClick={() => setBackgammon({ matchLength: value })}
                      className={`appearance-none m-0 cursor-pointer rounded-xl border px-2 py-2 text-center font-label-md text-label-md transition-colors ${
                        on
                          ? "border-secondary bg-secondary-container text-on-secondary-container"
                          : "border-outline-variant bg-surface-container text-on-surface-variant"
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              <p className="mt-0.5 font-caption text-caption leading-snug text-on-surface-variant">
                {fa.backgammon.matchLengthHint}
              </p>
            </Card>
            {/* PER-TURN WINDOW — تخته نرد runs one too, and it is REAL now: the server arms it
                beside the optional time bank and hands a seat to a bot after five lapsed turns.
                The first cut showed this picker and silently ignored it, which is worse than
                offering no control at all. A PUBLIC quick-match table is forced to 15. */}
            {turnTimerCard}
          </>
        ) : (
          turnTimerCard
        )}

        {/* حکم MATCH LENGTH — how many ROUNDS win the match. PRIVATE tables only: a public
            quick-match has no host to choose, so the server strips it there and always plays the
            canonical 7 (see TableController.normalizeSettings). Default 7. */}
        {activeGame === "hokm" && (
          <Card>
            <Heading icon="cards">{fa.hokm.chooseMatchLength}</Heading>
            <div className="mt-sm">
              <HokmMatchLengthSelect
                value={(settings.matchLength ?? DEFAULT_HOKM_MATCH_LENGTH) as HokmMatchLength}
                onChange={(v) => setSettings((st) => ({ ...st, matchLength: v }))}
              />
            </div>
            <p className="mt-0.5 font-caption text-caption leading-snug text-on-surface-variant">
              {fa.hokm.matchLengthHint}
            </p>
          </Card>
        )}

        {/* شلم MATCH TARGET — how many POINTS win the match (audit D13). PRIVATE tables only: a
            public quick-match has no host to choose, so the server FORCES ۶۶۰ there and refuses
            anything else (see TableController.normalizeSettings). Default ۶۶۰ (card-67). The solo
            start screen offers the same list in the same control. */}
        {isShelem && (
          <ChoiceRow
            icon="cards"
            label={fa.shelem.chooseTarget}
            options={SHELEM_TARGET_CHOICES}
            value={(settings.shelemTarget ?? DEFAULT_SHELEM_TARGET) as ShelemTarget}
            onChange={(shelemTarget) => setSettings((st) => ({ ...st, shelemTarget }))}
            hint={fa.shelem.targetHint}
          />
        )}

        {/* پاسور's SIZE AND MATCH TARGET, ONE ROW (card-pasur-cap): PRIVATE tables only. The size is how
            many play: ۴ (two partnerships) or ۲ (one against one): a public table's is the room the lobby's
            two «اتاق عمومی» buttons chose, and the server takes it from the ROOM TYPE this names
            (`pasur2-private` / `pasur-private`), so no client can send a size it would have to refuse.
            Default ۴, every time. The target is ۶۲, ۱۰۰ or ۱۰۴ points, default ۶۲: a public quick-match has no
            host to choose, so the server FORCES ۶۲ there; the solo start screen asks the same in the same
            control. Side by side so the card stands as tall as the size's row alone did and every row below
            stays where it was; where both headings do not fit on one line (zoomed text, a small Desktop window)
            a heading wraps and the card grows by that one line. Never stacked: that cost the operator's zoomed
            phone a whole row (journal: CARD-PASUR-CAP). */}
        {isPasur && (
          <Card>
            <div className="flex gap-x-2">
              <div className="flex flex-initial flex-col justify-between">
                <Heading icon="seats">{fa.pasur.chooseSeats}</Heading>
                <SegmentedChoice
                  label={fa.pasur.chooseSeats}
                  options={pasurSeatOptions}
                  value={pasurSeats}
                  onChange={setPasurSeats}
                  fit
                  className="mt-1.5"
                />
              </div>
              <div className="flex flex-auto flex-col justify-between">
                <Heading icon="cards">{fa.pasur.chooseTarget}</Heading>
                <SegmentedChoice
                  label={fa.pasur.chooseTarget}
                  options={PASUR_TARGET_NUMBER_CHOICES}
                  value={(settings.pasurTarget ?? DEFAULT_PASUR_TARGET) as PasurTarget}
                  onChange={(pasurTarget) => setSettings((st) => ({ ...st, pasurTarget }))}
                  fit
                  className="mt-1.5"
                />
              </div>
            </div>
            <p className="mt-1.5 m-0 text-caption text-on-surface-variant">{fa.pasur.seatsHint}</p>
          </Card>
        )}

        {/* هفت خبیث's LIMIT, PRIVATE tables only: a public table's is the server's, off this list. */}
        {isHaftkhabis && (
          <ChoiceRow
            icon="cards"
            label={fa.haftkhabis.chooseTarget}
            options={HAFTKHABIS_TARGET_NUMBER_CHOICES}
            value={(settings.haftkhabisTarget ?? DEFAULT_HAFTKHABIS_TARGET) as HaftkhabisTarget}
            onChange={(haftkhabisTarget) => setSettings((st) => ({ ...st, haftkhabisTarget }))}
            hint={fa.haftkhabis.targetHint}
          />
        )}

        {/* مار پله's SIZE — ۴ or ۲, PRIVATE tables only (a public table's size is the lobby button
            tapped). Its room type carries it, exactly as پاسور's does. */}
        {isLadders && (
          <ChoiceRow
            icon="seats"
            label={fa.ladders.players}
            options={LADDERS_PLAYER_CHOICES}
            value={laddersSeats}
            onChange={setLaddersSeats}
            hint={fa.ladders.playersHint}
          />
        )}

        {/* مار پله's LEVEL and LOOK (round 2) — PRIVATE tables only: a public table is always متوسط,
            with a look the server draws at random from the pictures (see
            TableController.normalizeSettings). The solo start screen offers the same two lists in
            the same control; the looks are only those this build can draw (`LADDERS_LIVE_STYLES`). */}
        {isLadders && (
          <ChoiceRow
            icon="level"
            label={fa.ladders.level}
            options={LADDERS_LEVEL_CHOICES}
            value={settings.laddersLevel ?? DEFAULT_LADDERS_LEVEL}
            onChange={(laddersLevel) => setSettings((st) => ({ ...st, laddersLevel }))}
          />
        )}
        {isLadders && (
          <ChoiceRow
            icon="look"
            label={fa.ladders.look}
            options={LADDERS_STYLE_CHOICES}
            value={settings.laddersStyle ?? DEFAULT_LADDERS_STYLE}
            onChange={(laddersStyle) => setSettings((st) => ({ ...st, laddersStyle }))}
          />
        )}

        {/* حکم کوت UNDER A SUIT-LESS CALL — a HOST switch, PRIVATE tables only.
            The sources treat this as a pre-game agreement rather than a rule (one of them ships a
            Hokm app with exactly this checkbox), so the table agrees it before play. OFF by
            default: the conventional reading, and what the platform already played. It changes
            NOTHING about a خال call — a 7–0 under a suit still pays 2 either way — which is why
            the hint says so rather than leaving a host to guess. A public quick-match table has
            no host to agree for the stranger who joins, so the server FORCES the default there
            (see TableController.normalizeSettings). */}
        {/* حکم SUIT-LESS CALLS — may the Hakem call سرس / نرس / آس نرس at this table at all?
            PRIVATE tables only, DEFAULT OFF (the standard game). It is the ENGINE that enforces it:
            the rule reaches `legalMoves`, so at a table that does not offer them the call is not
            merely hidden — it cannot be applied. A public quick-match has no host to agree for the
            stranger who joins, so the server FORCES it from the ROOM TYPE there («اتاق عمومی» off,
            «اتاق عمومی پیشرفته» on) and refuses a crafted value either way. */}
        {/* ⚠️ THE TWO CARDS SHARE ONE SLOT ON PURPOSE — they are a pair, not neighbours.
            `<main>` is a `gap-3` column, and a gap applies to a collapsed item just as much as to
            a tall one: leaving the reveal as its own child of that column would hold a 12px hole
            open on every modes-OFF table. So the pair is ONE item with no gap of its own, and the
            spacing between them (`pt-3`, the same 12px) lives INSIDE the reveal — where it
            collapses along with the card it belongs to. ⚠️ THE TWO MUST STAY EQUAL: they were both
            24px until 2026-09-01, and a reveal that opens to a different gap than its neighbours
            reads as a misaligned card rather than as the pair it is. 24 → 16 → 12 so far. */}
        {activeGame === "hokm" && (
          <div className="flex flex-col">
            <Card className="flex items-center justify-between gap-md">
              <div className="min-w-0">
                <Heading icon="cards">{fa.hokm.modes}</Heading>
                <p className="m-0 mt-0.5 font-caption text-caption leading-snug text-on-surface-variant">
                  {fa.hokm.modesHint}
                </p>
              </div>
              <Toggle
                checked={settings.hokmModes === true}
                label={fa.hokm.modes}
                onChange={() =>
                  setSettings((st) => ({ ...st, hokmModes: !(st.hokmModes === true) }))
                }
              />
            </Card>

            {/* …and the کوت question only EXISTS on a table that offers those calls, so it appears
                with them. Asking «does a سرس sweep pay a کوت?» at a table where سرس cannot be
                called is a setting with nothing to apply to. The stored value is left alone when
                the toggle is off — the server strips/forces it anyway, and a host who turns the
                modes back on should find their own answer where they left it.

                Wrapped in `Reveal` because a setting that APPEARS must appear: unwrapped, this
                card materialised in one frame and moved everything under it 121px. */}
            <Reveal open={settings.hokmModes === true}>
              <div className="pt-3">
                <Card className="flex items-center justify-between gap-md">
                  <div className="min-w-0">
                    <Heading icon="cards">{fa.hokm.modeKot}</Heading>
                    <p className="m-0 mt-0.5 font-caption text-caption leading-snug text-on-surface-variant">
                      {fa.hokm.modeKotHint}
                    </p>
                  </div>
                  <Toggle
                    checked={settings.hokmModeKot === true}
                    label={fa.hokm.modeKot}
                    onChange={() =>
                      setSettings((st) => ({ ...st, hokmModeKot: !(st.hokmModeKot === true) }))
                    }
                  />
                </Card>
              </div>
            </Reveal>
          </div>
        )}

        {/* Mute the table — an OWNER switch: game sound off for EVERY seated player. */}
        <Card className="flex items-center justify-between gap-md">
          <div className="min-w-0">
            <Heading icon="sound">{fa.online.muteTable}</Heading>
            <p className="m-0 mt-0.5 font-caption text-caption leading-snug text-on-surface-variant">
              {fa.online.muteTableHint}
            </p>
          </div>
          <Toggle
            checked={settings.muted}
            label={fa.online.muteTable}
            onChange={() => setSettings((s) => ({ ...s, muted: !s.muted }))}
          />
        </Card>

        {/* Table chat — an OWNER switch: allow the players to chat at the table. */}
        <Card className="flex items-center justify-between gap-md">
          <div className="min-w-0">
            <Heading icon="chat">{fa.online.chatEnabled}</Heading>
            <p className="m-0 mt-0.5 font-caption text-caption leading-snug text-on-surface-variant">
              {fa.online.chatEnabledHint}
            </p>
          </div>
          <Toggle
            checked={settings.chatAllowed}
            label={fa.online.chatEnabled}
            onChange={() => setSettings((s) => ({ ...s, chatAllowed: !s.chatAllowed }))}
          />
        </Card>

        {isChess ? (
          /* Chess: how a mid-game disconnect resolves (claim / bot / pause). `wrapLabels`: «جایگزینی
             ربات» is wider than a third of the row at text ×1.3 / ×1.5 and on Telegram Desktop's
             307×472, where it ran under «توقف» (card-98 item 13) — it breaks onto two lines there. */
          <ChoiceRow
            icon="disconnect"
            label={fa.chess.disconnect}
            options={dcOptions}
            value={chess.disconnectPolicy}
            onChange={(disconnectPolicy) => setChess({ disconnectPolicy })}
            wrapLabels
          />
        ) : isMensch ? (
          /* منچ: how many seats the table has — its one genuinely game-specific setting. */
          <Card>
            <Heading icon="seats">{fa.mensch.players}</Heading>
            <p className="m-0 mb-1.5 mt-0.5 font-caption text-caption leading-snug text-on-surface-variant">
              {fa.mensch.playersHint}
            </p>
            <MenschPlayersSelect
              value={menschPlayers}
              onChange={(n) => {
                setMenschPlayersLocal(n);
                setMenschPlayersOnline(n);
              }}
            />
          </Card>
        ) : isDooz ? (
          <>
            {/* How many دست the match runs. The server clamps it to 3/5 from the trusted room
                game, and forces three on a PUBLIC table — this picker only exists for a host. */}
            <Card>
              <Heading icon="seats">{fa.dooz.hands}</Heading>
              <DoozHandsSelect
                value={settings.matchLength ?? DEFAULT_DOOZ_MATCH_LENGTH}
                onChange={(n) => setSettings((s) => ({ ...s, matchLength: n }))}
              />
            </Card>
            {/*
              …and which colour YOUR pieces are.
              ⚠️ A TABLE SETTING, NOT A DEVICE ONE (operator, 2026-09-03). The host is seat 0, so
              their pick IS `doozColor` — the colour seat 0 plays in — and it rides the lobby echo
              to the guest, who takes the other. It used to be viewer-side with four options, which
              meant the two players could look at one cell and name two different colours. The
              local pref is still written so the SOLO start screen opens on the same choice.
            */}
            <Card>
              <Heading icon="cards">{fa.dooz.color}</Heading>
              <p className="m-0 mb-1.5 mt-0.5 font-caption text-caption leading-snug text-on-surface-variant">
                {fa.dooz.colorHint}
              </p>
              <DoozColorSelect
                value={settings.doozColor ?? doozColor}
                onChange={(c) => {
                  setDoozColorLocal(c);
                  setDoozColor(c);
                  setSettings((st) => ({ ...st, doozColor: c }));
                }}
              />
            </Card>
          </>
        ) : isBackgammon ? (
          <>
            {/* ITEM 12 — تخته نرد: which checkers you play with. Viewer-side (like a card back),
                so each player picks for their own screen and the two never disagree about the
                position — the offline start screen offers the identical picker. */}
            <Card>
              <Heading icon="cards">{fa.backgammon.color}</Heading>
              <p className="m-0 mb-1.5 mt-0.5 font-caption text-caption leading-snug text-on-surface-variant">
                {fa.backgammon.colorHint}
              </p>
              <BackgammonColorSelect
                value={bgColor}
                onChange={(c) => {
                  setBgColor(c);
                  setBackgammonColor(c);
                }}
              />
            </Card>
            {/* …and how a mid-game disconnect resolves — the OTHER backgammon setting the
                server enforces (claim / bot / pause), mirroring chess. */}
            <ChoiceRow
              icon="disconnect"
              label={fa.backgammon.disconnect}
              options={bgDcOptions}
              value={backgammon.disconnectPolicy}
              onChange={(disconnectPolicy) => setBackgammon({ disconnectPolicy })}
            />
          </>
        ) : usesCards ? (
          /* Card design — the back skin every seat at the table sees (card games only). */
          <Card>
            <Heading icon="cards">{fa.online.cardDeck}</Heading>
            <p className="m-0 mb-1.5 mt-0.5 font-caption text-caption leading-snug text-on-surface-variant">
              {fa.online.cardDeckHint}
            </p>
            <DeckPicker
              value={settings.deck}
              onChange={(deck) => setSettings((s) => ({ ...s, deck }))}
            />
          </Card>
        ) : null}
      </main>

      {/* Pinned CTA — full-width gold "create table". */}
      <div
        className="mx-auto w-full max-w-container-max shrink-0 px-md pt-3"
        style={{
          paddingBottom:
            "calc(max(env(safe-area-inset-bottom), var(--tg-safe-bottom, 0px)) + 12px)",
          backgroundImage:
            "linear-gradient(to top, rgb(var(--c-surface-container-lowest)) 35%, rgb(var(--c-surface-container-lowest) / 0))",
        }}
      >
        <button
          type="button"
          onClick={() => {
            // ISSUE 6 — a BOARD game's table has no cards, so «میز بساز» must not clatter a deck.
            // Board games get the low "a game begins" gong; card games keep the shuffle.
            playSfx(GAME_CATEGORY[activeGame as GameId] === "board" ? "boardStart" : "shuffle");
            createPrivate(
              activeGame,
              settings,
              isPasur ? pasurSeats : isLadders ? laddersSeats : undefined,
            );
          }}
          style={{ border: "none", borderTop: "1px solid rgb(var(--c-secondary) / 0.5)" }}
          className="flex w-full appearance-none cursor-pointer items-center justify-center gap-2 rounded-xl bg-gold-deep py-3 font-headline-sm text-headline-sm text-ink-on-gold shadow-[0_4px_14px_rgba(199,154,63,0.3)] transition-all hover:bg-secondary active:scale-95"
        >
          <span>{fa.online.createTable}</span>
          <Icon name="add" className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}
